import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth";
import { NAV_BADGES_KEY } from "@/hooks/useNavBadges";
import { Button } from "@/components/ui/Button";
import { whatsNewApi } from "../api/whatsNew";
import { KEYS } from "../hooks/useWhatsNew";
import { AnnouncementBody } from "./AnnouncementBody";
import type { LoginChanges } from "../types";

// Mirrors the backend gate (apps/announcements/views._login_changes_target) so
// ineligible users never even fire the query. The backend stays authoritative.
const WROCLAW_BRANCH = "wrocław";

// Same-browser cross-tab dismissal. Other browsers/devices are covered by polling.
const SYNC_CHANNEL = "elg5-login-changes";
const SYNC_STORAGE_KEY = "elg5-login-changes-confirmed";

const SCROLL_BOTTOM_TOLERANCE_PX = 24;

function formatDate(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Blocking "changes since last login" modal — mounted once in ProtectedLayout.
 *
 *  Shown to Wrocław coordinators/directors when there are unconfirmed published
 *  announcements. It cannot be dismissed until the list is scrolled to the very
 *  bottom and confirmed; confirming in any tab (or browser) hides it everywhere —
 *  instantly via BroadcastChannel/localStorage in this browser, via polling elsewhere. */
export function LoginChangesModalHost() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const eligible =
    !!user &&
    (user.role === "coordinator" || user.role === "director") &&
    !user.is_ceo &&
    !user.is_developer &&
    (user.branch?.name ?? "").trim().toLowerCase() === WROCLAW_BRANCH;

  const { data } = useQuery({
    queryKey: KEYS.loginChanges,
    queryFn: whatsNewApi.loginChanges,
    enabled: eligible,
    // Poll only while the modal is visible, so a confirmation made in another
    // browser/device dismisses it here too.
    refetchInterval: (query) => (query.state.data?.show ? 15_000 : false),
  });

  const dismiss = useCallback(() => {
    qc.setQueryData<LoginChanges>(KEYS.loginChanges, (old) =>
      old ? { ...old, show: false } : old,
    );
  }, [qc]);

  // Listen for a confirmation made in a sibling tab of the same browser.
  useEffect(() => {
    if (!eligible) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === SYNC_STORAGE_KEY && e.newValue) dismiss();
    };
    window.addEventListener("storage", onStorage);
    let channel: BroadcastChannel | null = null;
    if ("BroadcastChannel" in window) {
      channel = new BroadcastChannel(SYNC_CHANNEL);
      channel.onmessage = () => dismiss();
    }
    return () => {
      window.removeEventListener("storage", onStorage);
      channel?.close();
    };
  }, [eligible, dismiss]);

  const confirm = useMutation({
    mutationFn: whatsNewApi.confirmLoginChanges,
    onSuccess: () => {
      dismiss();
      try {
        if ("BroadcastChannel" in window) {
          const channel = new BroadcastChannel(SYNC_CHANNEL);
          channel.postMessage("confirmed");
          channel.close();
        }
        localStorage.setItem(SYNC_STORAGE_KEY, String(Date.now()));
      } catch {
        // Cross-tab sync is best-effort — polling catches up within 15 s.
      }
      // Read receipts changed → announcement lists and the unread badge are stale.
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: NAV_BADGES_KEY });
    },
  });

  const open = eligible && !!data?.show;

  // Scroll gate: the confirm button unlocks only after the list has been
  // scrolled to the very bottom (a list that fits without scrolling unlocks
  // immediately). Re-armed every time the modal (re)opens.
  const listRef = useRef<HTMLDivElement | null>(null);
  const [reachedBottom, setReachedBottom] = useState(false);

  const checkBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight <=
      SCROLL_BOTTOM_TOLERANCE_PX
    ) {
      setReachedBottom(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setReachedBottom(false);
    const raf = requestAnimationFrame(checkBottom);
    return () => cancelAnimationFrame(raf);
  }, [open, checkBottom]);

  if (!open) return null;

  const announcements = data?.announcements ?? [];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-changes-title"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-2xl">
        <div className="border-b border-[var(--color-border)] px-6 py-4">
          <h2
            id="login-changes-title"
            className="text-lg font-bold text-[var(--color-text-primary)]"
          >
            Zmiany od ostatniego logowania
          </h2>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Zapoznaj się z poniższą listą zmian i przewiń ją do samego dołu, aby
            móc zatwierdzić.
          </p>
        </div>
        <div
          ref={listRef}
          onScroll={checkBottom}
          className="flex-1 space-y-6 overflow-y-auto px-6 py-4"
        >
          {announcements.map((a) => (
            <article key={a.id}>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {a.title}
              </h3>
              {a.published_at && (
                <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                  Opublikowano {formatDate(a.published_at)}
                </p>
              )}
              <AnnouncementBody text={a.body} className="mt-2" />
            </article>
          ))}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-[var(--color-border)] px-6 py-4">
          {!reachedBottom && (
            <span className="text-xs text-[var(--color-text-secondary)]">
              Przewiń listę do końca, aby zatwierdzić
            </span>
          )}
          <Button
            type="button"
            disabled={!reachedBottom}
            loading={confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            Zatwierdź
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
